import React from 'react';
import { Shield } from 'lucide-react';
import MetricGroup, { MetricField } from '../MetricGroup';
import type { ServerStats } from '../../../types';
import { statsService } from '../../../core/StatsService';
import { getLevel, ThresholdLevel } from '../../../utils/thresholds';

interface Props {
  serverStats: ServerStats | null;
}

const Protection: React.FC<Props> = ({ serverStats }) => {
  // 30s 增量直接从 statsService 拿（兼容性维持，per_engine 增量留 Stage 3 扩展）
  const events30s = statsService.getEvents30s();

  const cpu = events30s.cpu_overload;
  const sudden = events30s.sudden_drop;
  const recovery = events30s.encoder_recovery;
  const skipBwe = events30s.frames_skipped_bwe;
  const skipGuard = events30s.frames_skipped_interval_guard;
  const largeFrame = events30s.large_frame;
  const bweDrop = events30s.bwe_drop_30plus;
  const kfForced = events30s.keyframe_forced;

  const cpuLevel: ThresholdLevel = getLevel('cpu_overload_30s', cpu);
  const suddenLevel: ThresholdLevel = getLevel('sudden_drop_30s', sudden);
  const recoveryLevel: ThresholdLevel = getLevel('encoder_recovery_30s', recovery);
  const skipBweLevel: ThresholdLevel = getLevel('frames_skipped_bwe_30s', skipBwe);
  const largeLevel: ThresholdLevel = getLevel('large_frame_30s', largeFrame);
  const bweDropLevel: ThresholdLevel = getLevel('bwe_drop_30plus_30s', bweDrop);

  const fields: MetricField[] = [
    { label: 'CPU 过载（30s）', value: cpu, unit: '次', level: cpuLevel,
      tooltip: '编码 >30ms 触发 CPU 过载保护降码率' },
    { label: 'BWE 突变（30s）', value: sudden, unit: '次', level: suddenLevel,
      tooltip: 'x264 sudden_drop（单步 30%+ 或 5s 累积 40%+）' },
    { label: 'BWE 跌幅 30%+', value: bweDrop, unit: '次', level: bweDropLevel },
    { label: '编码器重建', value: recovery, unit: '次', level: recoveryLevel,
      tooltip: '连续编码错误后触发重建，>0 即异常' },
    { label: 'BWE 跳帧', value: skipBwe, unit: '帧', level: skipBweLevel,
      tooltip: 'NVENC: 低码率时主动跳帧保画质' },
    { label: '帧间隔保护跳帧', value: skipGuard, unit: '帧',
      tooltip: '软编 Frame Interval Guard 跳帧（CPU 不够）' },
    { label: '大帧（>50KB）', value: largeFrame, unit: '次', level: largeLevel,
      tooltip: 'NVENC：单帧 >50KB 视为 burst 触发链路丢包' },
    { label: '关键帧产生', value: kfForced, unit: '次',
      tooltip: '30s 内编码器主动产关键帧次数' },
  ];

  // 卡片级别取最严重
  const allLevels = [cpuLevel, suddenLevel, recoveryLevel, skipBweLevel, largeLevel, bweDropLevel];
  const cardLevel: ThresholdLevel = allLevels.includes('crit')
    ? 'crit'
    : allLevels.includes('warn')
    ? 'warn'
    : 'normal';

  // 主指标：所有事件总数
  const totalEvents = cpu + sudden + recovery + largeFrame + bweDrop;

  return (
    <MetricGroup
      title="保护机制"
      icon={<Shield size={14} />}
      level={cardLevel}
      primary={{
        label: '关键事件总数（30s）',
        value: totalEvents,
        unit: '次',
        level: cardLevel,
      }}
      fields={fields}
    />
  );
};

export default Protection;
